import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminPilots, adminInvite, adminNewLink, adminRevoke, freshConversation } from "@/lib/api";
import { toast } from "sonner";

// Coach/admin console. Reached only by URL, protected by the admin secret (never stored beyond this tab).
export default function Admin() {
  const [secret, setSecret] = useState(() => sessionStorage.getItem("spark_admin") || "");
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [latest, setLatest] = useState(null);
  const [err, setErr] = useState("");

  const load = async (s = secret) => {
    try { setData(await adminPilots(s)); sessionStorage.setItem("spark_admin", s); setErr(""); }
    catch { setData(null); setErr("That secret didn't unlock the console."); }
  };
  useEffect(() => { if (secret) load(secret); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onFocus = () => { if (sessionStorage.getItem("spark_admin")) load(sessionStorage.getItem("spark_admin")); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async (e) => {
    e.preventDefault();
    try {
      const r = await adminInvite(secret, { email, name });
      setLatest({ ...r, label: `${name || email}` });
      setEmail(""); setName("");
      await load();
    } catch (ex) { toast(ex?.response?.data?.detail || "Couldn't create that invitation."); }
  };
  const newLink = async (p) => { const r = await adminNewLink(secret, p.user_id); setLatest({ ...r, label: p.name || p.email }); await load(); };
  const revoke = async (p) => { await adminRevoke(secret, p.user_id); toast("Sessions and links revoked."); await load(); };
  const copy = async (t) => { await navigator.clipboard?.writeText(t); toast("Link copied."); };

  return (
    <div className="min-h-screen" style={{ background: "hsl(var(--spark-canvas))" }}>
      <header data-testid="brand-header" className="bg-black text-white"><div className="spark-container flex items-center gap-4 h-[72px] sm:h-20"><img src="/brand/lockup_white.png" alt="Spark Coaching" className="h-9 sm:h-11 w-auto" /><span className="spark-eyebrow !text-white/60 ml-1 hidden sm:inline">Coach console</span>
        <div className="ml-auto flex items-center gap-4 text-xs">
          <Link to="/" data-testid="admin-back-link" className="text-white/70 hover:text-white">Back to Spark</Link>
          {data && <button data-testid="admin-lock-btn" onClick={() => { sessionStorage.removeItem("spark_admin"); setSecret(""); setData(null); }} className="text-white/70 hover:text-white">Lock console</button>}
        </div></div>
      </header>
      <main className="spark-container pt-12 pb-24 space-y-12">
        {!data ? (
          <form onSubmit={e => { e.preventDefault(); load(); }} className="max-w-md">
            <p className="spark-eyebrow mb-2">Admin secret</p>
            <input data-testid="admin-secret-input" type="password" value={secret} onChange={e => setSecret(e.target.value)} className="w-full rounded-2xl border border-[rgba(0,0,0,0.12)] bg-white px-5 py-4 focus:outline-none focus:border-[#2C50A0]" />
            <button data-testid="admin-unlock-btn" className="spark-btn-primary mt-4">Unlock</button>
            {err && <p data-testid="admin-error" className="mt-3 text-sm text-[#1F3D80]">{err}</p>}
          </form>
        ) : (
          <>
            <section className="grid md:grid-cols-12 gap-8">
              <div className="md:col-span-5">
                <p className="spark-eyebrow-copper spark-eyebrow-with-dot">Invite a pilot</p>
                <form onSubmit={invite} className="mt-4 space-y-3">
                  <input data-testid="admin-invite-name" placeholder="First name" value={name} onChange={e => setName(e.target.value)} className="w-full rounded-xl border border-[rgba(0,0,0,0.12)] bg-white px-4 py-3 text-sm" />
                  <input data-testid="admin-invite-email" type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full rounded-xl border border-[rgba(0,0,0,0.12)] bg-white px-4 py-3 text-sm" />
                  <button data-testid="admin-invite-btn" className="spark-btn-primary">Create invitation</button>
                </form>
                <p className="mt-4 text-xs text-[#8A8A91]">
                  {data.email_enabled ? "Email delivery is on — links are sent automatically via Resend." : "Email delivery isn't configured. Links appear here for you to send by email, text or WhatsApp. Add RESEND_API_KEY and SENDER_EMAIL to switch on automatic sending."}
                </p>
              </div>
              <div className="md:col-span-7">
                {latest && (
                  <div data-testid="admin-latest-link" className="rounded-2xl p-6" style={{ background: "hsl(var(--spark-warmth))" }}>
                    <p className="spark-eyebrow mb-2">{latest.sent ? "Sent by email to" : "Send this link to"} {latest.label}</p>
                    {latest.link ? (
                      <>
                        <code data-testid="admin-latest-link-url" className="block break-all text-sm text-[#17171C] bg-white/70 rounded-xl px-4 py-3">{latest.link}</code>
                        <button data-testid="admin-copy-link-btn" onClick={() => copy(latest.link)} className="spark-btn-secondary mt-3">Copy link</button>
                      </>
                    ) : <p className="text-sm text-[#56565D]">Delivered automatically.</p>}
                    <p className="mt-3 text-xs text-[#8A8A91]">One-time use · expires {new Date(latest.expires_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-4"><p className="spark-eyebrow">Pilots ({data.pilots.length})</p><button data-testid="admin-refresh-btn" onClick={() => load()} className="text-xs text-[#8A8A91] hover:text-[#1F3D80]">Refresh</button></div>
              <ul className="divide-y divide-[rgba(0,0,0,0.08)] border-t border-[rgba(0,0,0,0.08)]">
                {data.pilots.map(p => (
                  <li key={p.user_id} data-testid={`admin-pilot-${p.user_id}`} className="py-5 grid md:grid-cols-12 gap-4 items-start">
                    <div className="md:col-span-4">
                      <p className="text-[#17171C] font-medium">{p.name || "—"} <span className="text-xs text-[#8A8A91] ml-2">{p.plan}</span></p>
                      <p className="text-sm text-[#56565D]">{p.email}</p>
                      <p className="text-xs text-[#8A8A91] mt-1">{p.active_sessions} active session{p.active_sessions === 1 ? "" : "s"}</p>
                    </div>
                    <div className="md:col-span-5 text-sm">
                      {p.open_requests > 0 && (
                        <p data-testid="admin-open-request" className="text-[#1F3D80] mb-2">Asked for a sign-in link ({p.open_requests}) — create one and send it.</p>
                      )}
                      {p.pending_links.filter(l => l.link).map(l => (
                        <div key={l.id} className="mb-2">
                          <code className="block break-all text-xs text-[#56565D]">{l.link}</code>
                          <button onClick={() => copy(l.link)} className="text-xs text-[#8A8A91] hover:text-[#1F3D80]">Copy · expires {new Date(l.expires_at).toLocaleTimeString()}</button>
                        </div>
                      ))}
                    </div>
                    <div className="md:col-span-3 flex flex-wrap gap-2 md:justify-end">
                      <button data-testid={`admin-newlink-${p.user_id}`} onClick={() => newLink(p)} className="spark-btn-secondary text-xs px-4 py-2">New sign-in link</button>
                      <button data-testid={`admin-c1-fresh-${p.user_id}`} onClick={async () => { await freshConversation(p.user_id, secret); toast(`Conversation 1 restarted for ${p.name || p.email}. Their existing work is untouched.`); }} className="text-xs text-[#8A8A91] hover:text-[#1F3D80] px-2">Start Conversation 1 fresh</button>
                      <button data-testid={`admin-revoke-${p.user_id}`} onClick={() => revoke(p)} className="text-xs text-[#8A8A91] hover:text-[#1F3D80] px-2">Revoke</button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
            <p className="text-xs text-[#8A8A91]">Pilot journeys are private by default. This console never shows their reflections — only what they choose to share via "Share with my coach".</p>
          </>
        )}
      </main>
    </div>
  );
}
